import { api } from './api';

export type LabelStatus = 'active' | 'pending' | 'merged';

export interface LabelDefinition {
  id: string;
  key: string;
  domain: string | null;
  description: string | null;
  parent_key: string | null;
  synonyms: string[] | null;
  status: LabelStatus | null;
  merged_into: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProfileLabelEntry {
  id: string;
  type: 'FACT' | 'PREFERENCE';
  weight: number;
  context: string | null;
  labelKey: string;
  labelDescription: string | null;
  domain: string | null;
  status: LabelStatus | null;
}

export interface UserVectorProfile {
  userId: string;
  profileId: string;
  hasEmbeddings: { personality: boolean };
  hardFilters: {
    facts: Record<string, any>;
    preferences: Record<string, any>;
  };
  labels: ProfileLabelEntry[];
}

export interface LabelDefinitionsParams {
  page?: number;
  limit?: number;
  status?: LabelStatus | '';
  domain?: string;
  search?: string;
}

export interface LabelDefinitionsResponse {
  data: LabelDefinition[];
  total: number;
  page: number;
  limit: number;
  counts: { active: number; pending: number; merged: number };
}

export type TriageActionKind = 'merge' | 'promote' | 'discard';

export interface TriageAction {
  key: string;
  action: TriageActionKind;
  into?: string | null;
  canonical_key?: string | null;
  parent_key?: string | null;
  synonyms?: string[];
  domain?: string | null;
  description?: string | null;
  reason?: string | null;
}

export interface TriageProposalRow extends TriageAction {
  rowCount: number;
  description_current?: string | null;
}

export interface TriageProposal {
  actions: TriageProposalRow[];
  invalid: Array<{ action: TriageAction; problem: string }>;
  summary: {
    pendingTotal: number;
    merge: number;
    promote: number;
    discard: number;
    discardRows: number;
  };
}

export interface TriageReport {
  upserted: number;
  remapped: number;
  retired: number;
  orphanRisk: number;
  orphansRemaining: number;
  discarded: number;
  discardedRows: number;
  skippedInvalid: number;
}

export interface DuplicatePair {
  keyA: string;
  keyB: string;
  similarity: number;
  descriptionA: string | null;
  descriptionB: string | null;
  domainA: string | null;
  domainB: string | null;
  parentA: string | null;
  parentB: string | null;
  rowsA: number;
  rowsB: number;
}

export interface MergeActiveReport {
  from: string;
  into: string;
  remapped: number;
  childrenReparented: number;
  orphansRemaining: number;
}

export interface VectorSuggestionEntry {
  id: string;
  candidateId: string;
  score: number;
  reason: string | null;
  breakdown: {
    gated: boolean;
    gateReasons: string[];
    prefSatAtoB: number;
    prefSatBtoA: number;
    homophily: number;
    vibe: number | null;
    geo: number | null;
    final: number;
  } | null;
  feedback: number | null;
  createdAt: string;
}

// ── Match-reach / pool-distribution types (mirror server match-reach.ts) ─────

export interface MRHistogramBin {
  from: number;
  to: number | null;
  count: number;
  inRange: boolean;
}
export interface MRHistogram {
  bins: MRHistogramBin[];
  inRange: number;
  known: number;
  pct: number | null;
  soft?: boolean;
  unit?: string;
}
export interface MRFilterStat {
  active: boolean;
  pass: number;
  pct: number | null;
}
export interface MRFunnelStep {
  key: 'base' | 'age' | 'geo' | 'smoking' | 'religion' | 'matchable';
  label: string;
  count: number;
  pct: number;
}
export interface MatchReachResult {
  poolSize: number;
  genderEligible: number;
  matchable: number;
  matchablePct: number | null;
  funnel: MRFunnelStep[];
  filters: {
    age: MRFilterStat;
    geo: MRFilterStat & { isDealbreaker: boolean; withinRadius: number };
    smoking: MRFilterStat;
    religion: MRFilterStat;
  };
  histograms: {
    age: MRHistogram;
    height: MRHistogram;
    distanceMiles: MRHistogram;
  };
  bindingConstraint: {
    key: 'age' | 'geo' | 'smoking' | 'religion';
    label: string;
    removed: number;
  } | null;
}
export type UserMatchReach =
  | { hasSnapshot: false }
  | { hasSnapshot: true; generatedAt: string; result: MatchReachResult };

export interface PoolAggregates {
  poolSize: number;
  ageBins: { from: number; to: number | null; count: number }[];
  heightBins: { from: number; to: number | null; count: number }[];
  religionCounts: { value: string; count: number }[];
  smokingCounts: { value: string; count: number }[];
  topMetros: { city: string; count: number }[];
}
export interface PoolBucketSummary {
  gender: number;
  label: string;
  aggregates: PoolAggregates;
}
export interface PoolSnapshotSummary {
  generatedAt: string;
  pools: PoolBucketSummary[];
}
export type PoolSnapshotResponse =
  | { hasSnapshot: false }
  | ({ hasSnapshot: true } & PoolSnapshotSummary);

// ---- Vector operations (rollout steps run from the dashboard) -------------

export type VectorOpId =
  | 'cleanup-labels'
  | 'load-taxonomy'
  | 'reembed-labels'
  | 'migrate'
  | 'audit-constraints'
  | 'shadow';

export interface VectorOpDefinition {
  id: VectorOpId;
  step: number;
  title: string;
  description: string;
  /** Applying it changes data. */
  writes: boolean;
  preview: boolean;
  /** Calls an AI provider when applied. */
  usesAi: boolean;
  cli: string;
}

export type VectorOpStatus =
  | 'queued'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'cancelled';

export interface VectorOpRun {
  id: string;
  op: VectorOpId;
  dryRun: boolean;
  params: Record<string, unknown>;
  status: VectorOpStatus;
  progressDone: number;
  progressTotal: number | null;
  error: string | null;
  cancelRequested: boolean;
  startedBy: string | null;
  createdAt: string;
  startedAt: string | null;
  heartbeatAt: string | null;
  finishedAt: string | null;
  /** Running, but no progress for 15 minutes (the worker likely restarted). */
  stalled: boolean;
  /** Only on the single-run endpoint; shape depends on `op`. */
  result?: any;
}

export interface VectorOpsResponse {
  ops: VectorOpDefinition[];
  runs: VectorOpRun[];
}

export const vectorProfileApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getUserVectorProfile: builder.query<UserVectorProfile, string>({
      query: (userId) => `/admin/vector-profile/${userId}`,
      providesTags: (_result, _error, userId) => [
        { type: 'VectorProfile', id: userId },
      ],
    }),
    getLabelDefinitions: builder.query<
      LabelDefinitionsResponse,
      LabelDefinitionsParams | void
    >({
      query: (params) => {
        const p = params ?? {};
        const qs = new URLSearchParams();
        if (p.page) qs.set('page', String(p.page));
        if (p.limit) qs.set('limit', String(p.limit));
        if (p.status) qs.set('status', p.status);
        if (p.domain) qs.set('domain', p.domain);
        if (p.search?.trim()) qs.set('search', p.search.trim());
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        return `/admin/vector-profile/label-definitions${suffix}`;
      },
      providesTags: ['VectorLabels'],
    }),
    triageDryRun: builder.mutation<TriageProposal, void>({
      query: () => ({
        url: '/admin/vector-profile/taxonomy/triage/dry-run',
        method: 'POST',
      }),
    }),
    findDuplicates: builder.query<
      { data: DuplicatePair[]; threshold: number; limit: number },
      { threshold?: number } | void
    >({
      query: (params) => {
        const t = params?.threshold;
        return `/admin/vector-profile/taxonomy/duplicates${
          t ? `?threshold=${t}` : ''
        }`;
      },
      // Deliberately untagged: bulk-apply fires one merge mutation per pair,
      // and a tagged scan would rerun after every one. The modal rescans on
      // open instead.
    }),
    mergeActiveLabels: builder.mutation<
      MergeActiveReport,
      { from: string; into: string }
    >({
      query: (body) => ({
        url: '/admin/vector-profile/taxonomy/merge',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['VectorLabels', 'VectorProfile'],
    }),
    triageApply: builder.mutation<TriageReport, { actions: TriageAction[] }>({
      query: (body) => ({
        url: '/admin/vector-profile/taxonomy/triage/apply',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['VectorLabels', 'VectorProfile'],
    }),
    getUserVectorSuggestions: builder.query<
      { data: VectorSuggestionEntry[] },
      string
    >({
      query: (userId) => `/admin/vector-profile/${userId}/suggestions`,
      providesTags: (_result, _error, userId) => [
        { type: 'VectorProfile', id: userId },
      ],
    }),
    syncUserVectorProfile: builder.mutation<
      { success: boolean; userId: string },
      string
    >({
      query: (userId) => ({
        url: `/admin/vector-profile/${userId}/sync`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, userId) => [
        { type: 'VectorProfile', id: userId },
      ],
    }),
    getPoolSnapshot: builder.query<PoolSnapshotResponse, void>({
      query: () => '/admin/vector-profile/pool-snapshot',
      providesTags: ['PoolSnapshot'],
    }),
    generatePoolSnapshot: builder.mutation<PoolSnapshotSummary, void>({
      query: () => ({
        url: '/admin/vector-profile/pool-snapshot/generate',
        method: 'POST',
      }),
      invalidatesTags: ['PoolSnapshot'],
    }),
    getVectorOps: builder.query<VectorOpsResponse, void>({
      query: () => '/admin/vector-profile/ops',
      providesTags: ['VectorOps'],
    }),
    getVectorOpRun: builder.query<VectorOpRun, string>({
      query: (runId) => `/admin/vector-profile/ops/runs/${runId}`,
      providesTags: (_r, _e, runId) => [{ type: 'VectorOps', id: runId }],
    }),
    startVectorOp: builder.mutation<
      VectorOpRun,
      {
        op: VectorOpId;
        dryRun: boolean;
        params?: Record<string, unknown>;
      }
    >({
      query: ({ op, ...body }) => ({
        url: `/admin/vector-profile/ops/${op}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['VectorOps'],
    }),
    cancelVectorOp: builder.mutation<VectorOpRun, string>({
      query: (runId) => ({
        url: `/admin/vector-profile/ops/runs/${runId}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['VectorOps'],
    }),
    resumeVectorOp: builder.mutation<VectorOpRun, string>({
      query: (runId) => ({
        url: `/admin/vector-profile/ops/runs/${runId}/resume`,
        method: 'POST',
      }),
      invalidatesTags: ['VectorOps'],
    }),
    getUserMatchReach: builder.query<UserMatchReach, string>({
      query: (userId) => `/admin/vector-profile/${userId}/match-reach`,
      providesTags: (_result, _error, userId) => [
        { type: 'VectorProfile', id: userId },
        'PoolSnapshot',
      ],
    }),
  }),
});

export const {
  useGetUserVectorProfileQuery,
  useGetLabelDefinitionsQuery,
  useGetUserVectorSuggestionsQuery,
  useSyncUserVectorProfileMutation,
  useTriageDryRunMutation,
  useTriageApplyMutation,
  useLazyFindDuplicatesQuery,
  useMergeActiveLabelsMutation,
  useGetPoolSnapshotQuery,
  useGeneratePoolSnapshotMutation,
  useGetUserMatchReachQuery,
  useGetVectorOpsQuery,
  useGetVectorOpRunQuery,
  useStartVectorOpMutation,
  useCancelVectorOpMutation,
  useResumeVectorOpMutation,
} = vectorProfileApi;
